// react
import React, { useMemo, useState } from 'react';

// components
import { Tabs } from '@/components/atoms/Tabs/Tabs';
import { Title } from '@/components/atoms/Title/Title';
import { ProfilePreview } from '@/components/molecules/ProfilePreview/ProfilePreview';
import { CareCircle } from '@/components/molecules/CareCircle/CareCircle';

// store
import { CareCircle as CareCircleType } from '@karehero/models';

/**
 * CareCirclePage is the page for the Care Circle.
 */
export const CareCirclePage = ({ careCircle, onAdd }: CareCirclePageProps) => {
  // state
  const [activeTab, setActiveTab] = useState('overview');

  // memo
  const careRecipientName = useMemo(() => {
    return `${careCircle?.careRecipientAccount?.firstName} ${careCircle?.careRecipientAccount?.lastName}`;
  }, [careCircle]);

  const careRecipientFallback = useMemo(() => {
    return `${careCircle?.careRecipientAccount?.firstName[0]}${careCircle?.careRecipientAccount?.lastName[0]}`.toUpperCase();
  }, [careCircle]);

  const pluralName = useMemo(() => {
    if (!careRecipientName) {
      return "Care Recipient's";
    }
    if (careRecipientName?.endsWith('s')) {
      return `${careRecipientName}'`;
    }
    return `${careRecipientName}'s`;
  }, [careRecipientName]);

  const careCircleMembers = useMemo(() => {
    return careCircle?.careCircleMembers?.map((member) => ({
      ...member.account,
      roles: member.roles,
      id: member?.account?.id || 'overview',
      fallback:
        `${member?.account?.firstName?.[0]}${member?.account?.lastName?.[0]}`.toUpperCase(),
      onClick: () => {
        setActiveTab(member?.account?.id || 'overview');
      },
    }));
  }, [careCircle]);

  const tabs = useMemo(() => {
    return careCircleMembers?.map((member) => {
      const fallback =
        `${member?.firstName?.[0]}${member?.lastName?.[0]}`.toUpperCase();
      return {
        id: member.id,
        label: `${member.firstName} ${member.lastName}`,
        content: (
          <ProfilePreview
            imageSrc={member.imgSrc}
            fallback={fallback}
            name={`${member.firstName} ${member.lastName}`}
            email={member.email}
            careCircle={careRecipientName}
            isActionsDisabled={true}
          />
        ),
      };
    });
  }, [careCircleMembers, careRecipientName]);

  return (
    <>
      <Title>{pluralName} Care Circle</Title>
      {careCircle && careCircleMembers && tabs && (
        <Tabs
          value={activeTab}
          onChange={(val) => setActiveTab(val)}
          tabs={[
            {
              id: 'overview',
              label: 'Overview',
              content: (
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <CareCircle
                    careRecipientMember={{
                      id: careCircle.careRecipientAccount.id,
                      account: {
                        ...careCircle.careRecipientAccount,
                      },
                      fallback: careRecipientFallback,
                    }}
                    members={careCircleMembers.map((member) => ({
                      id: member.id,
                      account: { ...member },
                      roles: member.roles,
                      fallback: member.fallback,
                    }))}
                    onAdd={onAdd}
                  />
                </div>
              ),
            },
            ...tabs,
          ]}
        />
      )}
    </>
  );
};

interface CareCirclePageProps {
  careCircle?: CareCircleType;
  onAdd?: () => void;
}

CareCirclePage.displayName = 'CareCirclePage';

export default CareCirclePage;
